import { useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import type { SoftwareProfile } from '@/types/analysis';

export function useSoftwareProfiles() {
  const [profiles, setProfiles] = useLocalStorage<SoftwareProfile[]>('reddit-marketing:profiles', []);
  const [currentProfileId, setCurrentProfileId] = useLocalStorage<string | null>(
    'reddit-marketing:current-profile',
    null
  );

  // Get current profile
  const currentProfile = profiles.find((p) => p.id === currentProfileId) || null;

  // Create a new profile
  const createProfile = useCallback(
    (profileData: Omit<SoftwareProfile, 'id' | 'createdAt'>): SoftwareProfile => {
      const newProfile: SoftwareProfile = {
        ...profileData,
        id: `profile-${Date.now()}`,
        createdAt: Date.now(),
      };

      setProfiles([...profiles, newProfile]);
      setCurrentProfileId(newProfile.id);

      return newProfile;
    },
    [profiles, setProfiles, setCurrentProfileId]
  );

  // Update a profile
  const updateProfile = useCallback(
    (id: string, updates: Partial<Omit<SoftwareProfile, 'id' | 'createdAt'>>) => {
      setProfiles((prev) =>
        prev.map((profile) => (profile.id === id ? { ...profile, ...updates } : profile))
      );
    },
    [setProfiles]
  );

  // Delete a profile
  const deleteProfile = useCallback(
    (id: string) => {
      setProfiles((prev) => prev.filter((p) => p.id !== id));
      if (currentProfileId === id) {
        // Set to first remaining profile or null
        const remaining = profiles.filter((p) => p.id !== id);
        setCurrentProfileId(remaining.length > 0 ? remaining[0].id : null);
      }
    },
    [profiles, setProfiles, currentProfileId, setCurrentProfileId]
  );

  // Set current profile
  const setCurrentProfile = useCallback(
    (id: string | null) => {
      setCurrentProfileId(id);
    },
    [setCurrentProfileId]
  );

  return {
    profiles,
    currentProfile,
    currentProfileId,
    createProfile,
    updateProfile,
    deleteProfile,
    setCurrentProfile,
  };
}
